import React, { useState, useEffect, useRef } from 'react';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Accounts';
import Transactions from './pages/Transactions';
import Positions from './pages/Positions';
import Charts from './pages/Charts';
import Login from './pages/Login';
import Profile from './pages/Profile';
import Alerts from './pages/Alerts';
import Watchlist from './pages/Watchlist';
import Configuration from './pages/Configuration';
import Data from './pages/Data';
import EnvBanner from './components/EnvBanner';
import {
  DashboardIcon, PositionsIcon, TransactionsIcon, ChartIcon, BellIcon, WatchIcon,
  SettingsIcon, AccountsIcon, ProfileIcon, SignOutIcon, DataIcon,
} from './components/Icons';
import { isLoggedIn, clearToken, api } from './api/client';
import './App.css';

const NAV = [
  { id: 'dashboard',    label: 'Dashboard', icon: <DashboardIcon /> },
  { id: 'accounts',     label: 'Accounts', icon: <AccountsIcon /> },
  { id: 'positions',    label: 'Positions', icon: <PositionsIcon /> },
  { id: 'transactions', label: 'Transactions', icon: <TransactionsIcon /> },
  { id: 'charts',       label: 'Charts', icon: <ChartIcon /> },
  { id: 'alerts',       label: 'Alerts', icon: <BellIcon /> },
  { id: 'watchlist',    label: 'Watchlist', icon: <WatchIcon /> },
  { id: 'data',         label: 'Data', icon: <DataIcon /> },
];

const IDLE_CHECK_INTERVAL_MS = 30000;
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
const DEFAULT_SESSION_TIMEOUT_MINUTES = 15;

// Password-reset emails link to /?reset_token=...; read it once at load.
const RESET_TOKEN = new URLSearchParams(window.location.search).get('reset_token');

export default function App() {
  const [page, setPage] = useState('dashboard');
  const [navParams, setNavParams] = useState({});
  const [resetToken, setResetToken] = useState(RESET_TOKEN);
  const [loggedIn, setLoggedIn] = useState(isLoggedIn() && !RESET_TOKEN);
  const [unreadAlerts, setUnreadAlerts] = useState(0);
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = useState(DEFAULT_SESSION_TIMEOUT_MINUTES);
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    if (!loggedIn) return;
    const fetchUnread = () => api.getAlertEvents(true).then(e => setUnreadAlerts(e.length)).catch(() => {});
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [loggedIn]);

  useEffect(() => {
    if (!loggedIn) return;
    api.getMe().then(u => setSessionTimeoutMinutes(u.session_timeout_minutes)).catch(() => {});
  }, [loggedIn]);

  // Idle-logout: only real user interaction (not the alert-events poll above) counts as activity.
  useEffect(() => {
    if (!loggedIn) return;
    const markActive = () => { lastActivityRef.current = Date.now(); };
    markActive();
    ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, markActive));
    const interval = setInterval(() => {
      if (Date.now() - lastActivityRef.current >= sessionTimeoutMinutes * 60000) {
        handleLogout();
      }
    }, IDLE_CHECK_INTERVAL_MS);
    return () => {
      ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, markActive));
      clearInterval(interval);
    };
  }, [loggedIn, sessionTimeoutMinutes]);

  const handleLogin = () => {
    if (resetToken) {
      window.history.replaceState(null, '', window.location.pathname);
      setResetToken(null);
    }
    setLoggedIn(true);
  };
  const handleLogout = () => { clearToken(); setLoggedIn(false); };

  const navigateTo = (targetPage, params = {}) => {
    setNavParams(params);
    setPage(targetPage);
  };

  const handleNavClick = (id) => {
    setNavParams({});
    setPage(id);
  };

const renderPage = () => {
    switch (page) {
      case 'dashboard':    return <Dashboard onNavigate={navigateTo} />;
      case 'transactions': return <Transactions navParams={navParams} onNavigate={navigateTo} />;
      case 'positions':    return <Positions navParams={navParams} onNavigate={navigateTo} />;
      case 'charts':       return <Charts navParams={navParams} onNavigate={navigateTo} />;
      case 'alerts':       return <Alerts navParams={navParams} onNavigate={navigateTo} />;
      case 'watchlist':    return <Watchlist onNavigate={navigateTo} />;
      case 'data':         return <Data onNavigate={navigateTo} />;
      case 'configuration': return <Configuration />;
      case 'accounts':     return <Accounts onNavigate={navigateTo} />;
      case 'profile':      return <Profile />;
      default:             return <Dashboard onNavigate={navigateTo} />;
    }
  };

  if (!loggedIn) return <><EnvBanner /><Login onLogin={handleLogin} resetToken={resetToken} /></>;

  return (
    <>
    <EnvBanner />
    <div className="app-shell">
      <nav className="sidebar">
        <div className="sidebar-logo">Portfolio</div>
        {NAV.map(n => (
          <button key={n.id} className={`nav-item ${page === n.id ? 'active' : ''}`} onClick={() => handleNavClick(n.id)}>
            <span className="nav-icon">{n.icon}</span>
            <span>{n.label}</span>
            {n.id === 'alerts' && unreadAlerts > 0 && (
              <span style={{ marginLeft: 'auto', background: 'var(--loss)', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11 }}>{unreadAlerts}</span>
            )}
          </button>
        ))}
        <button className="nav-item" style={{ marginTop: 'auto' }} onClick={() => handleNavClick('configuration')}>
          <span className="nav-icon"><SettingsIcon /></span>
          <span>Configuration</span>
        </button>
        <button className="nav-item" onClick={() => handleNavClick('profile')}>
          <span className="nav-icon"><ProfileIcon /></span>
          <span>Profile</span>
        </button>
        <button className="nav-item" onClick={handleLogout}>
          <span className="nav-icon"><SignOutIcon /></span>
          <span>Sign Out</span>
        </button>
        </nav>
      <main className="main-content">{renderPage()}</main>
      <nav className="bottom-nav">
        {NAV.map(n => (
          <button key={n.id} className={`bottom-nav-item ${page === n.id ? 'active' : ''}`} onClick={() => handleNavClick(n.id)}>
            <span className="bottom-nav-icon">{n.icon}</span>
            <span>{n.label}</span>
            {n.id === 'alerts' && unreadAlerts > 0 && (
              <span style={{ background: 'var(--loss)', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 10 }}>{unreadAlerts}</span>
            )}
          </button>
        ))}
      </nav>
    </div>
    </>
  );
}